<template>
  <div>
    <Box borderRadius="medium" class="overflow-hidden h-60 fix-safari-border-radius">
      <Map
        v-if="mapShowing"
        :errorMessage="mapErrorMessage"
        :accessToken="mapBoxKey"
        :dragRotate="false"
        :hasDraggableMarker="hasDraggableMarker"
        :hasMarker="hasMarkerSet"
        :latitude="latitude || coords?.latitude"
        :longitude="longitude || coords?.longitude"
        :showZoomControls="true"
        :zoom="zoom"
        @onClick="handleOnClick"
        @update:latitudeLongitude="handleAddressLatLngUpdate"
      />
    </Box>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Box, Map } from '@aryeo/runway'

const props = defineProps({
  hasDraggableMarker: {
    type: Boolean,
    default: false,
  },
  latitude: {
    type: [Number, String],
    default: () => null,
  },
  longitude: {
    type: [Number, String],
    default: () => null,
  },
  mapErrorMessage: {
    type: String,
    default: () => '',
  },
  waitForNextTick: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['onChange', 'onClick'])

const mapShowing = ref(!props.waitForNextTick)

const hasMarkerSet = computed(
  () =>
    props.latitude !== '' &&
    props.latitude !== null &&
    props.longitude !== '' &&
    props.longitude !== null
)

const mapBoxKey = ref(window.ARYEO_MAPBOX_KEY)
// Map component not dynamically allowing zoom prop to change, so we adjust default cords slightly
// so we rerender map if no lat/lng provided
const coords = ref({
  latitude: 39.8283,
  longitude: -97.5795,
})
const defaultCenter = {
  latitude: 39.8283,
  longitude: -98.5795,
}
const zoom = ref(14)

onMounted(() => {
  if (!hasMarkerSet.value) {
    getLocation()
  }

  if (props.waitForNextTick) {
    nextTick(() => {
      mapShowing.value = true
    })
  }
})

// Geo locate
const getLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(showPosition, setToCenter)
  } else {
    setToCenter()
  }
}

const setToCenter = () => {
  zoom.value = 3
  coords.value = defaultCenter
}

const showPosition = (position) => {
  zoom.value = 7
  coords.value.latitude = position.coords.latitude
  coords.value.longitude = position.coords.longitude
}

const emitAddressChange = (lat, lng) => {
  emit('onChange', {
    key: 'is_map_dirty',
    value: true,
  })
  emit('onChange', {
    key: 'latitude',
    value: lat,
  })
  emit('onChange', {
    key: 'longitude',
    value: lng,
  })
}

const handleAddressLatLngUpdate = ({ lat, lng }) => {
  if (!hasMarkerSet.value) {
    return
  }

  emitAddressChange(lat, lng)
}

const handleOnClick = ({ lat, lng }) => {
  if (hasMarkerSet.value) {
    return
  }

  emitAddressChange(lat, lng)
}

watch(
  () => props.latitude,
  (newValue, oldValue) => {
    if (oldValue === '' || oldValue === null) {
      zoom.value = 14
    }
  }
)
</script>

<style scoped>
/* Fix border radius clipping absolutely position children in safari: https://bugs.webkit.org/show_bug.cgi?id=30475 */
.fix-safari-border-radius {
  -webkit-mask-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJAAAAAElFTkSuQmCC);
}
</style>
